import { MonthViewGrid } from '@/components/calendar/views/MonthViewGrid';
import { WeekTimeGrid, type CalendarVisualItem } from '@/components/calendar/views/WeekTimeGrid';
import { YearViewGrid } from '@/components/calendar/views/YearViewGrid';
import { formatMonthTitle } from '@/lib/calendarMonth';
import type { CalendarCell } from '@/lib/calendarMonth';
import type { ViewKind, WeekDayCell } from '@/lib/weekCalendar';
import type { DayAgendaItem } from '@/lib/calendarAgenda';

type Props = {
  view: ViewKind;
  anchorDate: Date;
  selectedDate: Date | null;
  monthCells: CalendarCell[];
  activeDays: WeekDayCell[];
  allVisualItems: CalendarVisualItem[];
  attendanceByDay?: Record<string, 'present' | 'absent' | 'weekoff' | 'holiday'> | null;
  compactYearLayout: boolean;
  onSelectDate: (date: Date) => void;
  onSelectMonth: (monthAnchor: Date) => void;
  onOpenItemPopup: (date: Date, key: { id: string; kind: DayAgendaItem['kind'] }) => void;
};

export function CalendarViewport({
  view,
  anchorDate,
  selectedDate,
  monthCells,
  activeDays,
  allVisualItems,
  attendanceByDay,
  compactYearLayout,
  onSelectDate,
  onSelectMonth,
  onOpenItemPopup,
}: Props) {
  if (view === 'year') {
    return (
      <YearViewGrid
        year={anchorDate.getFullYear()}
        selectedDate={selectedDate}
        items={allVisualItems}
        attendanceByDay={attendanceByDay ?? null}
        compact={compactYearLayout}
        onSelectDate={onSelectDate}
        onSelectMonth={onSelectMonth}
      />
    );
  }

  if (view === 'month') {
    return (
      <MonthViewGrid
        monthTitle={formatMonthTitle(anchorDate)}
        cells={monthCells}
        items={allVisualItems}
        selectedDate={selectedDate}
        attendanceByDay={attendanceByDay ?? null}
        onSelectDate={(d) => onSelectDate(d)}
        onItemClick={(item, d) => onOpenItemPopup(d, { id: item.agendaId ?? item.id, kind: item.kind })}
        onMoreClick={(d) => onSelectDate(d)}
      />
    );
  }

  return (
    <WeekTimeGrid
      days={activeDays}
      items={allVisualItems}
      selectedDate={selectedDate}
      attendanceByDay={attendanceByDay ?? null}
      onSlotClick={onSelectDate}
      onDayHeaderClick={onSelectDate}
      onItemClick={(item, d) => onOpenItemPopup(d, { id: item.agendaId ?? item.id, kind: item.kind })}
    />
  );
}
